/**
 * BillingAlerts - Configure billing alerts and notifications
 *
 * Features:
 * - Configure alert thresholds (failed payments, churn, MRR drops)
 * - Set notification recipients
 * - Enable/disable specific alerts
 * - Test alert delivery
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Bell,
  AlertTriangle,
  TrendingDown,
  CreditCard,
  DollarSign,
  CheckCircle2,
  Save,
} from 'lucide-react';

interface AlertSettings {
  failed_payment_alert_enabled: boolean;
  failed_payment_recipients: string[];
  churn_alert_enabled: boolean;
  churn_threshold_percentage: number;
  churn_alert_recipients: string[];
  mrr_drop_alert_enabled: boolean;
  mrr_drop_threshold_percentage: number;
  mrr_drop_recipients: string[];
  low_balance_alert_enabled: boolean;
  low_balance_threshold: number;
  low_balance_recipients: string[];
}

export function BillingAlerts() {
  const queryClient = useQueryClient();

  // Fetch alert settings
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['admin-billing-alert-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calie_admin_get_billing_alert_settings');
      if (error) throw error;
      // RPC returns an array, get first row
      const row = Array.isArray(data) ? data[0] : data;
      return row as AlertSettings;
    },
  });

  const { register, handleSubmit, watch, setValue } = useForm<AlertSettings>({
    values: settings,
  });

  // Update alert settings mutation
  const updateMutation = useMutation({
    mutationFn: async (data: AlertSettings) => {
      const { error } = await supabase.rpc('calie_admin_update_billing_alert_settings', {
        p_failed_payment_alert_enabled: data.failed_payment_alert_enabled,
        p_failed_payment_recipients: data.failed_payment_recipients,
        p_churn_alert_enabled: data.churn_alert_enabled,
        p_churn_threshold_percentage: data.churn_threshold_percentage,
        p_churn_alert_recipients: data.churn_alert_recipients,
        p_mrr_drop_alert_enabled: data.mrr_drop_alert_enabled,
        p_mrr_drop_threshold_percentage: data.mrr_drop_threshold_percentage,
        p_mrr_drop_recipients: data.mrr_drop_recipients,
        p_low_balance_alert_enabled: data.low_balance_alert_enabled,
        p_low_balance_threshold: data.low_balance_threshold,
        p_low_balance_recipients: data.low_balance_recipients,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-billing-alert-settings'] });
      toast.success('Alert settings updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update alert settings: ${error.message}`);
    },
  });

  // Test alert mutation
  const testAlertMutation = useMutation({
    mutationFn: async (alertType: string) => {
      const { error } = await supabase.rpc('calie_admin_test_billing_alert', {
        p_alert_type: alertType,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Test alert sent successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to send test alert: ${error.message}`);
    },
  });

  const onSubmit = (data: AlertSettings) => {
    updateMutation.mutate(data);
  };

  const handleTestAlert = (alertType: string) => {
    testAlertMutation.mutate(alertType);
  };

  const parseEmailList = (value: string): string[] => {
    return value.split(',').map((email) => email.trim()).filter(Boolean);
  };

  const formatEmailList = (emails: string[]): string => {
    return emails.join(', ');
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <p>Failed to load alert settings: {(error as Error).message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          Billing Alert Configuration
        </CardTitle>
        <CardDescription>
          Configure automated alerts for billing events and anomalies
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Failed Payment Alerts */}
          <div className="space-y-4 p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-red-600" />
                <div>
                  <h3 className="font-semibold">Failed Payment Alerts</h3>
                  <p className="text-sm text-muted-foreground">
                    Notify when customer payments fail
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={watch('failed_payment_alert_enabled')}
                  onCheckedChange={(checked) => setValue('failed_payment_alert_enabled', checked)}
                />
                {watch('failed_payment_alert_enabled') && (
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                )}
              </div>
            </div>
            {watch('failed_payment_alert_enabled') && (
              <div className="space-y-2 ml-8">
                <Label htmlFor="failed_payment_recipients">Recipients (comma-separated emails)</Label>
                <div className="flex gap-2">
                  <Input
                    id="failed_payment_recipients"
                    type="text"
                    placeholder="admin@example.com, finance@example.com"
                    value={formatEmailList(watch('failed_payment_recipients') || [])}
                    onChange={(e) => setValue('failed_payment_recipients', parseEmailList(e.target.value))}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestAlert('failed_payment')}
                    disabled={testAlertMutation.isPending}
                  >
                    Test
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Churn Alerts */}
          <div className="space-y-4 p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingDown className="h-5 w-5 text-amber-600" />
                <div>
                  <h3 className="font-semibold">Churn Rate Alerts</h3>
                  <p className="text-sm text-muted-foreground">
                    Alert when churn rate exceeds threshold
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={watch('churn_alert_enabled')}
                  onCheckedChange={(checked) => setValue('churn_alert_enabled', checked)}
                />
                {watch('churn_alert_enabled') && (
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                )}
              </div>
            </div>
            {watch('churn_alert_enabled') && (
              <div className="space-y-3 ml-8">
                <div className="space-y-2">
                  <Label htmlFor="churn_threshold">Threshold (%)</Label>
                  <Input
                    id="churn_threshold"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    {...register('churn_threshold_percentage', { valueAsNumber: true })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="churn_recipients">Recipients (comma-separated emails)</Label>
                  <div className="flex gap-2">
                    <Input
                      id="churn_recipients"
                      type="text"
                      placeholder="admin@example.com"
                      value={formatEmailList(watch('churn_alert_recipients') || [])}
                      onChange={(e) => setValue('churn_alert_recipients', parseEmailList(e.target.value))}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestAlert('churn')}
                      disabled={testAlertMutation.isPending}
                    >
                      Test
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* MRR Drop Alerts */}
          <div className="space-y-4 p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-red-600" />
                <div>
                  <h3 className="font-semibold">MRR Drop Alerts</h3>
                  <p className="text-sm text-muted-foreground">
                    Alert when MRR drops by specified percentage
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={watch('mrr_drop_alert_enabled')}
                  onCheckedChange={(checked) => setValue('mrr_drop_alert_enabled', checked)}
                />
                {watch('mrr_drop_alert_enabled') && (
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                )}
              </div>
            </div>
            {watch('mrr_drop_alert_enabled') && (
              <div className="space-y-3 ml-8">
                <div className="space-y-2">
                  <Label htmlFor="mrr_drop_threshold">Threshold (%)</Label>
                  <Input
                    id="mrr_drop_threshold"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    {...register('mrr_drop_threshold_percentage', { valueAsNumber: true })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mrr_drop_recipients">Recipients (comma-separated emails)</Label>
                  <div className="flex gap-2">
                    <Input
                      id="mrr_drop_recipients"
                      type="text"
                      placeholder="admin@example.com"
                      value={formatEmailList(watch('mrr_drop_recipients') || [])}
                      onChange={(e) => setValue('mrr_drop_recipients', parseEmailList(e.target.value))}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestAlert('mrr_drop')}
                      disabled={testAlertMutation.isPending}
                    >
                      Test
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Low Balance Alerts */}
          <div className="space-y-4 p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <h3 className="font-semibold">Low Balance Alerts</h3>
                  <p className="text-sm text-muted-foreground">
                    Alert when tenant balance falls below threshold
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={watch('low_balance_alert_enabled')}
                  onCheckedChange={(checked) => setValue('low_balance_alert_enabled', checked)}
                />
                {watch('low_balance_alert_enabled') && (
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                )}
              </div>
            </div>
            {watch('low_balance_alert_enabled') && (
              <div className="space-y-3 ml-8">
                <div className="space-y-2">
                  <Label htmlFor="low_balance_threshold">Threshold (USD)</Label>
                  <Input
                    id="low_balance_threshold"
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('low_balance_threshold', { valueAsNumber: true })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="low_balance_recipients">Recipients (comma-separated emails)</Label>
                  <div className="flex gap-2">
                    <Input
                      id="low_balance_recipients"
                      type="text"
                      placeholder="admin@example.com"
                      value={formatEmailList(watch('low_balance_recipients') || [])}
                      onChange={(e) => setValue('low_balance_recipients', parseEmailList(e.target.value))}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestAlert('low_balance')}
                      disabled={testAlertMutation.isPending}
                    >
                      Test
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <Button type="submit" disabled={updateMutation.isPending}>
              <Save className="h-4 w-4 mr-2" />
              Save Alert Settings
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
